import { Chart, type ChartBlockProps, type ChartSpec } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/* ── The flat BE#2577 contract (EPIC #188) — what the runtime emits and the
 * BE validates/persists. `Chart` reads it directly (no `spec` wrapper). ── */

const flatBar: ChartBlockProps = {
  chart_type: "bar",
  title: "Content views by experience",
  subtitle: "Top 5, last 90 days",
  x: { key: "name", label: "Experience", type: "category" },
  series: [{ key: "views", label: "Views", color: null }],
  data: [
    { name: "Flavors of Thailand", views: 708 },
    { name: "Azure Mentor", views: 320 },
    { name: "Pasta Weeknights", views: 260 },
    { name: "Intro to K2", views: 121 },
    { name: "Studio Tour", views: 95 },
  ],
  value_format: "integer",
  source_note: "From studio stats",
  summary: "Flavors of Thailand leads at 708 views; the rest trail under 350.",
};

const flatStackedBar: ChartBlockProps = {
  chart_type: "bar",
  stacked: true,
  title: "Revenue by product line",
  x: { key: "quarter", label: "Quarter", type: "category" },
  series: [
    { key: "subscriptions", label: "Subscriptions", color: null },
    { key: "services", label: "Services", color: null },
    { key: "addons", label: "Add-ons", color: null },
  ],
  data: [
    { quarter: "Q1", subscriptions: 42000, services: 18000, addons: 6000 },
    { quarter: "Q2", subscriptions: 48000, services: 21000, addons: 9000 },
    { quarter: "Q3", subscriptions: 56000, services: 19000, addons: 12000 },
    { quarter: "Q4", subscriptions: 64000, services: 24000, addons: 15000 },
  ],
  value_format: "currency",
  summary: "Quarterly revenue grew every quarter, led by subscriptions.",
};

const flatHorizontalBar: ChartBlockProps = {
  chart_type: "bar",
  orientation: "horizontal",
  title: "Top studios by members",
  x: { key: "studio", label: "Studio", type: "category" },
  series: [{ key: "members", label: "Members", color: null }],
  data: [
    { studio: "Flavors of Thailand", members: 1240 },
    { studio: "Azure Certification Mentor", members: 890 },
    { studio: "Pasta Weeknights", members: 555 },
    { studio: "Studio Tour", members: 210 },
  ],
  value_format: "integer",
  summary: "Flavors of Thailand has the largest membership at 1,240.",
};

const flatPie: ChartBlockProps = {
  chart_type: "pie",
  title: "Content by type",
  x: { key: "kind", label: "Type", type: "category" },
  series: [{ key: "count", label: "Count", color: null }],
  data: [
    { kind: "Notes", count: 34 },
    { kind: "Videos", count: 21 },
    { kind: "Quizzes", count: 13 },
    { kind: "Podcasts", count: 8 },
  ],
  value_format: "integer",
  summary: "Notes are the largest content type at 34 of 76 items.",
};

// `donut` is a renderer-side extension: the BE strips unknown props, so it
// works for host-constructed blocks / direct <Chart> use, not (yet) blocks
// that round-trip the chat pipeline.
const flatDonut: ChartBlockProps = { ...flatPie, title: "Content by type (donut)", donut: true };

const flatScatter: ChartBlockProps = {
  chart_type: "scatter",
  title: "Engagement vs completion",
  subtitle: "One point per experience",
  x: { key: "engagement", label: "Engagement", type: "number" },
  series: [{ key: "completion", label: "Completion", color: null }],
  data: [
    { engagement: 12, completion: 84 },
    { engagement: 26, completion: 71 },
    { engagement: 38, completion: 63 },
    { engagement: 44, completion: 58 },
    { engagement: 61, completion: 49 },
    { engagement: 78, completion: 44 },
  ],
  value_format: "percent",
  summary: "Completion falls as engagement time rises - longer experiences lose more learners.",
};

const flatStackedArea: ChartBlockProps = {
  chart_type: "area",
  stacked: true,
  title: "Weekly active learners",
  x: { key: "week", label: "Week", type: "time" },
  series: [
    { key: "mobile", label: "Mobile", color: null },
    { key: "desktop", label: "Desktop", color: null },
  ],
  data: [
    { week: "W1", mobile: 120, desktop: 200 },
    { week: "W2", mobile: 160, desktop: 210 },
    { week: "W3", mobile: 190, desktop: 190 },
    { week: "W4", mobile: 240, desktop: 205 },
  ],
  value_format: "integer",
  summary: "Mobile usage doubled over four weeks while desktop stayed flat.",
};

/* ── The legacy nested spec — still fully supported (`{ spec }` shape from
 * older hosts / direct consumers keeps rendering unchanged). ── */

const nestedBar: ChartSpec = {
  type: "bar",
  title: "Weekly signups",
  description: "New accounts per day (legacy nested spec)",
  data: [
    { day: "Mon", signups: 12, activations: 8 },
    { day: "Tue", signups: 19, activations: 13 },
    { day: "Wed", signups: 9, activations: 6 },
    { day: "Thu", signups: 22, activations: 15 },
    { day: "Fri", signups: 27, activations: 19 },
  ],
  xKey: "day",
  series: [
    { key: "signups", label: "Signups" },
    { key: "activations", label: "Activations" },
  ],
  showLegend: true,
};

const nestedLine: ChartSpec = {
  type: "line",
  title: "Latency (p95)",
  data: [
    { t: "00:00", ms: 180 },
    { t: "04:00", ms: 210 },
    { t: "08:00", ms: 320 },
    { t: "12:00", ms: 260 },
    { t: "16:00", ms: 240 },
    { t: "20:00", ms: 200 },
  ],
  xKey: "t",
  series: [{ key: "ms", label: "ms" }],
};

export function ChartDemo() {
  return (
    <Section
      id="chart"
      title="Chart"
      description="Themed recharts chart for the K2 `chart` block. Reads the flat BE#2577 contract (chart_type / x / series / data / value_format / summary) as the primary shape AND the legacy nested `spec`. All five families: bar (grouped / stacked / horizontal), line, area (stackable), pie (donut-able), scatter. Series default to the --chart-1..5 brand tokens."
    >
      <VariantBlock label="Bar - flat block" caption="value_format=integer + summary + source_note">
        <div className="w-full max-w-xl">
          <Chart spec={flatBar} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Bar - stacked (flat)"
        caption="stacked: true, value_format=currency (USD)"
      >
        <div className="w-full max-w-xl">
          <Chart spec={flatStackedBar} />
        </div>
      </VariantBlock>

      <VariantBlock label="Bar - horizontal (flat)" caption='orientation: "horizontal"'>
        <div className="w-full max-w-xl">
          <Chart spec={flatHorizontalBar} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Pie + donut (flat)"
        caption="single-series; donut is a renderer-side flag"
      >
        <div className="grid w-full max-w-3xl gap-4 sm:grid-cols-2">
          <Chart spec={flatPie} />
          <Chart spec={flatDonut} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Scatter (flat)"
        caption='x.type: "number" -> numeric axes; value_format=percent'
      >
        <div className="w-full max-w-xl">
          <Chart spec={flatScatter} />
        </div>
      </VariantBlock>

      <VariantBlock label="Area - stacked (flat)">
        <div className="w-full max-w-xl">
          <Chart spec={flatStackedArea} />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Legacy nested spec"
        caption="the pre-1.8 `{ spec }` shape keeps rendering"
      >
        <div className="w-full max-w-xl space-y-4">
          <Chart spec={nestedBar} />
          <Chart spec={nestedLine} />
        </div>
      </VariantBlock>

      <VariantBlock label="States">
        <div className="w-full max-w-xl space-y-4">
          <Chart spec={{ ...flatPie, data: [] }} />
          <Chart spec={nestedBar} loading />
          <Chart spec={nestedBar} error />
        </div>
      </VariantBlock>
    </Section>
  );
}
